<script>
import { MANAGEMENT, NORMAN } from '@shell/config/types';
import ArrayList from '@shell/components/form/ArrayList';
import Principal from '@shell/components/auth/Principal';
import Loading from '@shell/components/Loading';
import { _CREATE, _VIEW } from '@shell/config/query-params';
import { get, set } from '@shell/utils/object';
import { membershipIdentity } from '@shell/utils/membership-identity';

function normalizeId(id) {
  return id?.replace(':', '/') || id;
}

export function canViewMembershipEditor(store, needsProject = false) {
  return (!!store.getters['management/schemaFor'](MANAGEMENT.PROJECT_ROLE_TEMPLATE_BINDING) || !needsProject) &&
    !!store.getters['management/schemaFor'](MANAGEMENT.ROLE_TEMPLATE) &&
    !!store.getters['rancher/schemaFor'](NORMAN.PRINCIPAL);
}

export default {
  emits: ['membership-update'],

  components: {
    ArrayList, Loading, Principal
  },

  props: {
    addMemberDialogName: {
      type:     String,
      required: true
    },

    parentKey: {
      type:     String,
      required: true
    },

    parentId: {
      type:    String,
      default: null
    },

    mode: {
      type:     String,
      required: true
    },

    type: {
      type:     String,
      required: true
    },

    defaultBindingHandler: {
      type:    Function,
      default: null,
    },

    modalSticky: {
      type:    Boolean,
      default: false,
    }
  },

  async fetch() {
    const roleBindingRequestParams = { type: this.type, opt: { force: true } };

    if (this.type === NORMAN.PROJECT_ROLE_TEMPLATE_BINDING && this.parentId) {
      Object.assign(roleBindingRequestParams, { opt: { filter: { projectId: this.parentId.split('/').join(':') }, force: true } });
    }
    const userHydration = [
      this.schema ? this.$store.dispatch(`rancher/findAll`, roleBindingRequestParams) : [],
      this.$store.dispatch('rancher/findAll', { type: NORMAN.PRINCIPAL }),
      this.$store.dispatch(`management/findAll`, { type: MANAGEMENT.ROLE_TEMPLATE }),
      this.$store.dispatch(`management/findAll`, { type: MANAGEMENT.USER })
    ];
    const [allBindings] = await Promise.all(userHydration);

    const bindings = allBindings
      .filter((b) => normalizeId(get(b, this.parentKey)) === normalizeId(this.parentId));

    this['lastSavedBindings'] = [...bindings];

    // Add the current user as the project owner. This will get created by default
    if (this.mode === _CREATE && bindings.length === 0 && this.defaultBindingHandler) {
      const defaultBinding = await this.defaultBindingHandler();

      defaultBinding.isDefaultBinding = true;
      bindings.push(defaultBinding);
    }

    this['bindings'] = bindings;
  },

  data() {
    return {
      schema:            this.$store.getters[`rancher/schemaFor`](this.type),
      bindings:          [],
      lastSavedBindings: [],
    };
  },

  computed: {
    memberIdentities() {
      const users = this.$store.getters['management/all']?.(MANAGEMENT.USER) || [];

      return this.bindings.map((binding) => membershipIdentity(binding, users));
    },
    newBindings() {
      return this.bindings
        .filter((binding) => !binding.id && !this.lastSavedBindings.includes(binding) && !binding.isDefaultBinding);
    },
    removedBindings() {
      return this.lastSavedBindings
        .filter((binding) => !this.bindings.includes(binding));
    },
    membershipUpdate() {
      const newBindings = this.newBindings;
      const removedBindings = this.removedBindings;

      return {
        newBindings:     this.newBindings,
        removedBindings: this.removedBindings,
        save:            (parentId) => {
          const savedPromises = newBindings.map((binding) => {
            set(binding, this.parentKey, parentId);

            return binding.save();
          });

          const removedPromises = removedBindings.map((binding) => binding.remove());

          return Promise.all([...savedPromises, ...removedPromises]);
        }
      };
    },

    isCreate() {
      return this.mode === _CREATE;
    },

    isView() {
      return this.mode === _VIEW;
    },
  },
  watch: {
    membershipUpdate: {
      deep: true,
      handler() {
        this.$emit('membership-update', this.membershipUpdate);
      }
    }
  },

  methods: {
    addMember() {
      this.$store.dispatch('cluster/promptModal', {
        component:      this.addMemberDialogName,
        componentProps: { onAdd: this.onAddMember },
        modalSticky:    this.modalSticky
      });
    },

    onAddMember(bindings) {
      this['bindings'] = [...this.bindings, ...bindings];
    },
  }
};
</script>
<template>
  <Loading v-if="$fetchState.pending" />
  <ArrayList
    v-else
    v-model:value="bindings"
    :class="['membership-editor', {'membership-editor-view': isView}]"
    :mode="mode"
    :show-header="true"
  >
    <template #column-headers>
      <div class="box mb-0 membership-header">
        <div class="column-headers row membership-columns">
          <div class="col span-6">
            <label class="text-label">{{ t('membershipEditor.user') }}</label>
          </div>
          <div class="col span-6">
            <label class="text-label">{{ t('membershipEditor.role') }}</label>
          </div>
        </div>
      </div>
    </template>
    <template #columns="{row, i}">
      <div class="columns row membership-columns">
        <div class="col span-6">
          <Principal
            v-if="memberIdentities[i] && memberIdentities[i].principalId"
            :value="memberIdentities[i].principalId"
          />
          <div
            v-else
            class="member-identity-fallback"
            :data-testid="`member-identity-${i}`"
          >
            <div class="member-avatar">
              <i
                :class="['icon', memberIdentities[i] && memberIdentities[i].unavailable ? 'icon-warning' : 'icon-user']"
                aria-hidden="true"
              />
            </div>
            <div class="member-name">
              {{ memberIdentities[i] && !memberIdentities[i].unavailable ? memberIdentities[i].label : t('principal.error') }}
            </div>
            <div class="member-identifier">
              {{ memberIdentities[i] && memberIdentities[i].identifier }}
            </div>
          </div>
        </div>
        <div
          :data-testid="`role-item-${i}`"
          class="col span-6 role"
        >
          {{ row.value.roleDisplay }}
        </div>
      </div>
    </template>
    <template #add>
      <button
        type="button"
        class="btn role-primary mt-10"
        data-testid="add-item"
        @click="addMember"
      >
        {{ t('generic.add') }}
      </button>
    </template>
    <template #remove-button="{remove, i}">
      <span v-if="(isCreate && i === 0) || isView" />
      <button
        v-else
        type="button"
        :disabled="isView"
        class="btn role-link"
        :data-testid="`remove-item-${i}`"
        @click="remove"
      >
        {{ t('generic.remove') }}
      </button>
    </template>
  </ArrayList>
</template>

<style lang="scss" scoped>
.membership-editor {
  font-size: 13px;

  .membership-header {
    display: block;
    padding: 12px 16px;
    margin: 0;
    border: 1px solid var(--dev-border, var(--border));
    border-radius: 6px 6px 0 0;
    background: var(--dev-layer, var(--body-bg));

    .text-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--dev-muted, var(--muted));
    }
  }

  &:not(.membership-editor-view) .membership-header {
    // Match ArrayList's existing action column so User/Role headers align with
    // their values while keeping Remove in its native column.
    padding-right: calc(16px + #{$array-list-remove-margin});
  }

  // These are native flex rows. Keep their span widths and gutters: the legacy
  // row clearfix pseudo-elements must never become extra grid cells.
  .membership-columns {
    align-items: center;
    min-width: 0;
    margin: 0;

    > .col {
      min-width: 0;
    }
  }

  :deep(.box[role="group"]) {
    padding: 14px 16px;
    margin: 0;
    border: 1px solid var(--dev-border, var(--border));
    border-top: 0;
    background: var(--dev-surface, var(--body-bg));
  }

  :deep(.principal) {
    grid-template-columns: 32px minmax(0, 1fr);
    grid-template-rows: auto auto;
    column-gap: 10px;
    align-items: center;
    min-width: 0;
    font-size: 13px;

    .avatar, .avatar img, .avatar .empty {
      width: 32px;
      height: 32px;
      min-width: 32px;
      max-width: 32px;
      min-height: 32px;
      max-height: 32px;
      padding: 0;
    }

    .avatar img {
      object-fit: contain;
    }

    .avatar .empty {
      line-height: 32px;
    }

    .name, .description {
      line-height: 1.45;
      overflow-wrap: anywhere;
    }

    .description {
      font-size: 12px;
    }
  }

  .member-identity-fallback {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    grid-template-areas: "avatar name" "avatar identifier";
    column-gap: 10px;
    align-items: center;
    min-width: 0;

    .member-avatar {
      grid-area: avatar;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 6px;
      background: var(--dev-layer, var(--body-bg));
      color: var(--dev-muted, var(--muted));
    }

    .member-name {
      grid-area: name;
      font-size: 13px;
      line-height: 1.45;
      overflow-wrap: anywhere;
    }

    .member-identifier {
      grid-area: identifier;
      font-size: 12px;
      line-height: 1.45;
      overflow-wrap: anywhere;
      color: var(--dev-muted, var(--muted));
    }
  }

  :deep(.footer) {
    padding-top: 8px;
  }
}

.role {
  display: flex;
  align-items: center;
  flex-direction: row;
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

@media (max-width: 760px) {
  .membership-editor :deep(.box[role="group"]) {
    padding: 12px;
  }
}
</style>
